import { ChangeEvent, useEffect, useState, useMemo } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import { getPharmaciesByCompanyId } from "@/dispatchers/locationDispatchers";
import { getCompanyMarketplaceApps } from "@/dispatchers/marketAppDispacher";
import { getmembers } from "@/dispatchers/memberDispatchers";

export interface MarketplaceApp {
  id: string;
  name: string;
  marketType: number;
  description: string;
  price: string;
  imageUrl?: string;
  imageEmoji?: string;
  category: string;
  features: string[];
  developer: string;
  requirements: string;
  isActive: boolean;
}

export type Location = {
  id: string;
  name: string;
  address: string;
  active: boolean;
  ods: string;
};

type User = {
  id: string;
  title?: string;
  forename: string;
  surname: string;
  email: string;
  role: string;
  active: boolean;
};

interface InstalledAppData {
  locations: string[];
  apiKeys: Record<string, any>;
}

function mapPharmaciesToUi(data: any): Location[] {
  const list = Array.isArray(data) ? data : data ? [data] : [];
  return list
    .filter((p) => p?.active === true)
    .map((p) => ({
      id: p?.id ?? p?.pharmacyId ?? crypto.randomUUID(),
      name: p?.pharmacyName ?? p?.name ?? "Pharmacy",
      address: normalizeAddress(p?.pharmacyAddress ?? p?.address),
      active: p?.active ?? true,
      ods: p?.ods,
    }));
}

function normalizeAddress(addr: any): string {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  const parts = [
    addr.line1,
    addr.line2,
    addr.city,
    addr.postcode,
    addr.postalCode,
    addr.country,
  ].filter(Boolean);
  return parts.join(", ");
}

export const useMarketAppSettingsData = () => {
  const { appState, marketAppState, pharmacyServiceState } = useTitanApp();
  const { darkMode, companyId, currentUser } = appState || {};
  const { marketplaceAppsData, isMarketAppsLoading } = marketAppState || {};
  const isDarkMode = darkMode ?? false;

  const [marketplaceApps, setMarketApps] = useState<MarketplaceApp[]>([]);
  const [installedApps, setInstalledApps] = useState<
    Map<string, InstalledAppData>
  >(new Map());
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const getTitleString = (titleEnum: number): string => {
    const titleMap: Record<number, string> = {
      0: "",
      1: "Mr",
      2: "Mrs",
      3: "Miss",
      4: "Ms",
    };
    return titleMap[titleEnum] || "";
  };

  useEffect(() => {
    if (marketplaceApps && Array.isArray(marketplaceApps)) {
      setMarketApps(
        marketplaceAppsData
          .filter((app: any) => app.isActive === true)
          .map((app: any) => ({
            id: app._id ?? "",
            name: app.appName ?? "",
            marketType: app.marketType,
            description: app.description ?? "",
            price: app.price?.toString() ?? "tbc",
            imageUrl: app.imageUrl ?? "",
            imageEmoji: app.imageEmoji ?? "",
            category: app.category ?? "",
            features: app.features ?? [],
            developer: app.developer ?? "",
            requirements: app.requirements ?? "",
            isActive: app.isActive,
          })),
      );
    }
  }, [marketplaceAppsData]);

  useEffect(() => {
    const fetchPharmacies = async () => {
      if (!companyId) return;
      try {
        const pharmaciesData = await getPharmaciesByCompanyId(companyId);
        const mappedLocations = mapPharmaciesToUi(pharmaciesData);
        setLocations(mappedLocations);
      } catch (error) {
        console.error("Error fetching pharmacies:", error);
      }
    };
    fetchPharmacies();
  }, [companyId]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!companyId) return;
      try {
        const membersData = await getmembers(companyId);
        const mappedUsers: User[] = membersData.map((member: any) => ({
          id: member._id,
          title: getTitleString(member.title ?? 0),
          forename: member.firstName || "",
          surname: member.lastName || "",
          email: member.emailAddress || "",
          role: member.role?.roleName || "",
          active: member.active ?? true,
        }));
        setUsers(mappedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [companyId]);

  const fetchInstalledApps = async () => {
    if (!companyId || locations.length === 0) return;

    try {
      const data = await getCompanyMarketplaceApps(companyId);

      const appMap = new Map<string, InstalledAppData>();

      data.forEach((entry: any) => {
        const appId = entry.marketAppID;
        const ods = entry.ods;
        const payloadRaw = entry.payLoad;
        const marketType = entry.marketType;
        const email = entry.email;

        if (!appId) {
          return;
        }

        if (marketType === 4) {
          let payload;
          if (payloadRaw) {
            try {
              payload = JSON.parse(payloadRaw);
            } catch (err) {
              return;
            }
          }

          const existing = appMap.get(appId) || {
            locations: ["company"],
            apiKeys: {},
          };

          existing.apiKeys["company"] = {
            secretKey: payload?.secretKey || "",
          };

          appMap.set(appId, existing);
          return;
        }

        if (marketType === 1) {
          const user = users.find((u) => u.email === email);
          if (!user) return;

          const userId = user.id;

          let payload;
          if (payloadRaw) {
            try {
              payload = JSON.parse(payloadRaw);
            } catch (err) {
              return;
            }
          }

          const existing = appMap.get(appId) || {
            locations: [],
            apiKeys: {},
          };

          existing.apiKeys[userId] = {
            apiKey: payload?.licenseKey || "",
            email: payload?.email || user.email,
          };

          appMap.set(appId, existing);
          return;
        }

        const location = locations.find((loc) => loc.ods === ods);
        if (!location) return;

        const locationId = location.id;

        let payload;
        if (payloadRaw) {
          try {
            payload = JSON.parse(payloadRaw);
          } catch (err) {
            return;
          }
        }

        const existing = appMap.get(appId) || {
          locations: [],
          apiKeys: {},
        };

        existing.locations.push(locationId);

        if (marketType === 0) {
          existing.apiKeys[locationId] = {
            isAccountRegistered: entry.isAccountRegistered || false,
            payload: payload || {},
          };
        } else {
          existing.apiKeys[locationId] = {
            apiKey: payload?.licenseKey || "",
            email: payload?.email || "",
          };
        }

        appMap.set(appId, existing);
      });

      setInstalledApps(appMap);
    } catch (err) {}
  };

  useEffect(() => {
    fetchInstalledApps();
  }, [companyId, locations.length, users.length]);

  const refreshInstalledApps = () => {
    fetchInstalledApps();
  };

  return {
    isDarkMode,
    marketplaceApps,
    locations,
    users,
    companyId,
    installedApps,
    refreshInstalledApps,
    isMarketAppsLoading,
    currentUser,
  };
};
